import { useState, useCallback, useRef } from 'react';
import { apiClient, DuplicateFileError } from '../clients/APIClient';
import { useToastStore } from '../stores/ToastStore';
import type { ToastType } from '../types/ui';
import { useDocumentStore } from '../stores/documentStore';
import { useChatListStore } from '../stores/chatListStore';

interface UseMagazineImportOptions {
	adaptToModal?: boolean;
	placement?: 'chat' | 'bottom-center';
	onComplete?: () => void | Promise<void>;
}

export interface MagazineImportProgress {
	isImporting: boolean;
	currentFile: string;
	currentIndex: number;
	totalFiles: number;
	percentage: number;
}

const IDLE_PROGRESS: MagazineImportProgress = {
	isImporting: false,
	currentFile: '',
	currentIndex: 0,
	totalFiles: 0,
	percentage: 0,
};

const plural = (n: number) => (n > 1 ? 's' : '');

// Module-scoped so concurrent drops across hook instances collapse to one.
let importRunning = false;

export function useMagazineImport(options: UseMagazineImportOptions = {}) {
	const { showToast } = useToastStore();
	const updateDocumentContext = useDocumentStore((s) => s.updateDocumentContext);
	const [isImporting, setIsImporting] = useState(false);
	const [importProgress, setImportProgress] = useState<MagazineImportProgress>(IDLE_PROGRESS);

	const onCompleteRef = useRef(options.onComplete);
	onCompleteRef.current = options.onComplete;

	const importMagazines = useCallback(
		async (files: File[]) => {
			if (importRunning) {
				showToast('Magazine import already in progress', 'info', {
					timeout: 2000,
					adaptToModal: options.adaptToModal,
					placement: options.placement,
				});
				return;
			}
			importRunning = true;

			try {
				const magFiles = files.filter((f) => f.name.toLowerCase().endsWith('.mag'));
				if (magFiles.length === 0) return;

				const totalFiles = magFiles.length;
				setIsImporting(true);
				setImportProgress({ ...IDLE_PROGRESS, isImporting: true, totalFiles });

				// Persistent loading toast : replaced by per-file progress, then by the final summary.
				const loadingToastOpts = {
					adaptToModal: options.adaptToModal,
					placement: options.placement,
				};
				const renderProgressToast = (current: string, idx: number, total: number, pct: number) => {
					const counter = total > 1 ? ` (${idx}/${total})` : '';
					showToast(`Importing ${current}${counter} - ${pct}%`, 'loading', loadingToastOpts);
				};
				showToast(
					`Importing ${totalFiles} magazine file${plural(totalFiles)}...`,
					'loading',
					loadingToastOpts,
				);
				// Tracks the highest percentage shown so the toast never regresses across SSE events.
				let lastPercentage = 0;

				let successCount = 0;
				let duplicateCount = 0;
				let errorCount = 0;
				const warnings: Array<{ file: string; failedNames: string[] }> = [];

				try {
					for (let i = 0; i < magFiles.length; i += 1) {
						const file = magFiles[i];

						const baselinePercentage = Math.round((i / totalFiles) * 100);
						lastPercentage = Math.max(lastPercentage, baselinePercentage);
						setImportProgress((prev) => ({
							...prev,
							currentFile: file.name,
							currentIndex: i + 1,
							percentage: baselinePercentage,
						}));
						renderProgressToast(file.name, i + 1, totalFiles, lastPercentage);

						// Correlation token : listener skips chunks from other uploads on the shared bus.
						const requestId =
							typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
								? crypto.randomUUID()
								: `mag-${Date.now()}-${Math.random().toString(36).slice(2)}`;

						let fileChunkCount = 0;
						const fileFailedNames: string[] = [];
						const handleProgress = (event: Event) => {
							const { detail } = event as CustomEvent;
							if (!detail) return;
							if (detail.requestId !== requestId) return;

							// Embedding-model-mismatch warning : record skipped doc names but don't tick progress.
							if (detail.status === 'warning' && Array.isArray(detail.failed_names)) {
								detail.failed_names.forEach((name: unknown) => {
									if (typeof name === 'string') fileFailedNames.push(name);
								});
								return;
							}

							fileChunkCount += 1;

							const outerFraction = Math.max(0, i / totalFiles);
							const innerFraction =
								Number.isFinite(detail.progress) && detail.progress >= 0
									? Math.min(detail.progress, 1) / totalFiles
									: // Fallback when backend omits progress: approach 95% over 20 chunks.
									  (Math.min(fileChunkCount, 20) / 20) * 0.95 / totalFiles;
							const nextComputed = Math.max(
								0,
								Math.min(Math.round((outerFraction + innerFraction) * 100), 99),
							);
							lastPercentage = Math.max(lastPercentage, nextComputed);
							const percentage = lastPercentage;
							setImportProgress((prev) => ({ ...prev, percentage }));
							renderProgressToast(file.name, i + 1, totalFiles, percentage);
						};

						window.addEventListener('file-upload-progress', handleProgress);
						try {
							// Sequential by design : per-file progress UI and module-scoped
							// importRunning flag both assume serial processing.
							// eslint-disable-next-line no-await-in-loop
							const result = await apiClient.files.uploadMagazine(file, {}, { requestId });
							if (result && typeof result.id === 'string' && result.id.length > 0) {
								successCount += 1;
							} else {
								console.warn('[MagazineImport] uploadMagazine returned no id for:', file.name);
								errorCount += 1;
							}
						} catch (error) {
							if (error instanceof DuplicateFileError) {
								duplicateCount += 1;
							} else {
								console.error(`Failed to import magazine file ${file.name}:`, error); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
								errorCount += 1;
							}
						} finally {
							window.removeEventListener('file-upload-progress', handleProgress);
							if (fileFailedNames.length > 0) {
								warnings.push({ file: file.name, failedNames: fileFailedNames });
							}
							// Snap on every terminal state ; duplicates fire no chunks, so no handler tick.
							setImportProgress((prev) => ({
								...prev,
								percentage: Math.max(prev.percentage, Math.round(((i + 1) / totalFiles) * 100)),
							}));
						}
					}

					await useChatListStore.getState().refreshChats();
					await updateDocumentContext({ operation: 'refresh', shouldRefreshServer: true });

					try {
						await onCompleteRef.current?.();
					} catch (err) {
						console.error('[MagazineImport] onComplete callback threw:', err);
					}

					const toastOpts = {
						timeout: 3000,
						adaptToModal: options.adaptToModal,
						placement: options.placement,
					};
					const summaries: Array<[number, ToastType, string]> = [
						[
							successCount,
							'success',
							`Successfully imported ${successCount} magazine file${plural(successCount)}`,
						],
						[
							duplicateCount,
							'info',
							`${duplicateCount} magazine file${plural(duplicateCount)} already up to date - any missing collections were restored`,
						],
						[
							errorCount,
							'error',
							`Failed to import ${errorCount} magazine file${plural(errorCount)}`,
						],
					];
					summaries.forEach(([count, type, message]) => {
						if (count > 0) {
							try {
								showToast(message, type, toastOpts);
							} catch (err) {
								console.error(`[MagazineImport] summary toast failed (type: ${type}):`, err); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
							}
						}
					});

					if (warnings.length > 0) {
						const skippedTotal = warnings.reduce(
							(n, w) => n + w.failedNames.length,
							0,
						);
						try {
							showToast(
								`${skippedTotal} document${plural(skippedTotal)} skipped due to embedding model mismatch`,
								'warning',
								{ ...toastOpts, timeout: 6000 },
							);
						} catch (err) {
							console.error('[MagazineImport] warning toast failed:', err);
						}
					}
				} finally {
					setIsImporting(false);
					setImportProgress(IDLE_PROGRESS);
				}
			} finally {
				importRunning = false;
			}
		},
		[showToast, updateDocumentContext, options.adaptToModal, options.placement],
	);

	return { importMagazines, isImporting, importProgress };
}
